import { useQueryClient } from '@tanstack/react-query'
import { createContext, ReactNode, use } from 'react'
import useWebSocket, { ReadyState, SendMessage } from 'react-use-websocket'

import env from '~/env'

import { addEvents } from './handleWebSocketUpdates'
import { isCustomHandlerType, useWebSocketHandlers } from './useWebSocketHandlers'

const socketUrl = env.VITE_API_BASE_URL.replace('https', 'wss') + '/ws'

const connectionStatusMap = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
} as const

type ConnectionStatus = (typeof connectionStatusMap)[keyof typeof connectionStatusMap]

type MessageHandler = (data: unknown) => void

type WebSocketContextValue = {
    sendMessage: SendMessage
    readyState: ReadyState
    connectionStatus: ConnectionStatus
    registerMessageHandler: (type: string, handler: MessageHandler) => () => void
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

export function WebSocketProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient()
    const { registerMessageHandler, handleCustomMessage } = useWebSocketHandlers()

    const handleMessage = (data: string) => {
        if (data === 'pong') return

        try {
            const message = JSON.parse(data)

            if (isCustomHandlerType(message)) handleCustomMessage(message)
            else addEvents(data, queryClient)
        } catch (error) {
            console.error('Expected JSON for WebSocket event, received:', error)
            return
        }
    }

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        share: true,
        onMessage: event => handleMessage(event.data),
        shouldReconnect: () => true,
        heartbeat: env.VITE_HINT_WEBSOCKET
            ? {
                  interval: 5000,
                  message: 'ping',
                  returnMessage: 'pong',
                  timeout: 10000,
              }
            : false,
    })

    const value: WebSocketContextValue = {
        sendMessage,
        readyState,
        connectionStatus: connectionStatusMap[readyState],
        registerMessageHandler,
    }

    return <WebSocketContext value={value}>{children}</WebSocketContext>
}

export function useWebSocketConnection(): WebSocketContextValue {
    const context = use(WebSocketContext)
    if (!context) {
        throw new Error('useWebSocketConnection must be used within a WebSocketProvider')
    }
    return context
}
